import { FC } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components';
import { useTrans } from '@libs/i18n';
import { BuildingsIcon } from '@phosphor-icons/react';

import { completionTone } from './profileNudge.utils';

const AUTO_DISMISS_MS = 7000;

interface LoginCompletionTooltipProps {
  open: boolean;
  progressPct: number;
  /** "Complete Profile" — navigates to the profile hub. */
  onComplete: () => void;
  /** "Later" / auto-dismiss / close. */
  onClose: () => void;
}

/**
 * Login nudge: a small top-right card surfacing the user's Organization Profile
 * completion right after they sign in. Auto-dismisses after a few seconds.
 * Presentational — the mount point owns the show-once-per-login decision.
 *
 * Built on the shared {@link TooltipCard} (the `default` variant in floating
 * mode) so it shares the app-wide tooltip surface, theme and behaviour.
 */
export const LoginCompletionTooltip: FC<LoginCompletionTooltipProps> = observer(({
  open,
  progressPct,
  onComplete,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTrans();

  const accent = theme.palette[completionTone(progressPct)].main;

  return (
    <TooltipCard
      floating
      variant="default"
      size="lg"
      open={open}
      onClose={onClose}
      showClose
      autoHideDuration={AUTO_DISMISS_MS}
      floatingPosition={{ top: 60, right: 20 }}
      maxWidth={300}
      icon={<BuildingsIcon size={18} />}
      title={t('app.orgProfile.nudge.tooltip.greeting')}
      description={
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.25 }}>
            {t('app.orgProfile.nudge.tooltip.body', { percent: progressPct })}
          </Typography>

          <Box
            sx={{
              height: 4,
              borderRadius: 999,
              overflow: 'hidden',
              backgroundColor: alpha(accent, 0.25),
            }}
          >
            <Box sx={{ height: '100%', width: `${progressPct}%`, borderRadius: 999, backgroundColor: accent }} />
          </Box>
        </Box>
      }
      actions={
        <>
          <Button variant="primary" onClick={onComplete} sx={{ flex: 1 }}>
            {t('app.orgProfile.nudge.tooltip.complete')}
          </Button>
          <Button variant="tertiary" onClick={onClose}>
            {t('app.orgProfile.nudge.tooltip.later')}
          </Button>
        </>
      }
    />
  );
});
